import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Building2, Phone, ShoppingBag, HelpCircle, FileText, Plus, Trash2,
  Edit2, Save, X, LogOut, Loader2, CheckCircle, AlertCircle, Layers
} from 'lucide-react';
import './Admin.css';

const TABS = [
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'contact', label: 'Contact', icon: Phone },
  { id: 'products', label: 'Products', icon: ShoppingBag },
  { id: 'faqs', label: 'FAQs', icon: HelpCircle },
  { id: 'policies', label: 'Policies', icon: FileText },
  { id: 'sections', label: 'Custom Sections', icon: Layers },
];

const Toast = ({ msg, type }) => (
  <div className={`admin-toast admin-toast--${type}`}>
    {type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
    {msg}
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('company');
  const [kb, setKb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchKB = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/kb');
      setKb(res.data.data);
    } catch {
      showToast('Failed to load knowledge base', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKB(); }, [fetchKB]);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const save = async (endpoint, data) => {
    setSaving(true);
    try {
      await api.put(endpoint, data);
      showToast('Saved successfully');
      fetchKB();
    } catch {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="admin-loader">
      <Loader2 size={32} className="admin-spin" />
      <p>Loading knowledge base…</p>
    </div>
  );

  return (
    <div className="admin-layout">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <Building2 size={20} />
          <span>KB Admin</span>
        </div>
        <nav className="admin-nav">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`admin-nav-item ${tab === id ? 'admin-nav-item--active' : ''}`}
              onClick={() => setTab(id)}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <span className="admin-sidebar-user">👤 {user?.username}</span>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={handleLogout}>
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          {tab === 'company' && <CompanyTab data={kb?.company} onSave={d => save('/api/admin/kb/company', d)} saving={saving} />}
          {tab === 'contact' && <ContactTab data={kb?.contact} onSave={d => save('/api/admin/kb/contact', d)} saving={saving} />}
          {tab === 'products' && <ProductsTab data={kb?.products} refresh={fetchKB} showToast={showToast} />}
          {tab === 'faqs' && <FAQsTab data={kb?.faqs} refresh={fetchKB} showToast={showToast} />}
          {tab === 'policies' && <PoliciesTab data={kb?.policies} onSave={d => save('/api/admin/kb/policies', d)} saving={saving} />}
          {tab === 'sections' && <SectionsTab data={kb?.custom_sections} refresh={fetchKB} showToast={showToast} />}
        </div>
      </main>
    </div>
  );
}

// ── Company Tab ─────────────────────────────────────────────────────
function CompanyTab({ data, onSave, saving }) {
  const [form, setForm] = useState(data || {});
  useEffect(() => { if (data) setForm(data); }, [data]);

  return (
    <TabWrapper title="Company Information" subtitle="Basic info about your company shown to the AI.">
      <div className="admin-grid-2">
        <Field label="Company Name" value={form.name || ''} onChange={v => setForm(f => ({ ...f, name: v }))} />
        <Field label="Tagline" value={form.tagline || ''} onChange={v => setForm(f => ({ ...f, tagline: v }))} />
        <Field label="Website" value={form.website || ''} onChange={v => setForm(f => ({ ...f, website: v }))} />
        <Field label="Founded Year" value={form.founded || ''} onChange={v => setForm(f => ({ ...f, founded: v }))} />
      </div>
      <Field label="Description" value={form.description || ''} onChange={v => setForm(f => ({ ...f, description: v }))} multiline />
      <SaveBtn onClick={() => onSave(form)} saving={saving} />
    </TabWrapper>
  );
}

// ── Contact Tab ─────────────────────────────────────────────────────
function ContactTab({ data, onSave, saving }) {
  const [form, setForm] = useState(data || {});
  useEffect(() => { if (data) setForm(data); }, [data]);

  return (
    <TabWrapper title="Contact Information" subtitle="How customers can reach you.">
      <div className="admin-grid-2">
        <Field label="Email" value={form.email || ''} onChange={v => setForm(f => ({ ...f, email: v }))} />
        <Field label="Phone" value={form.phone || ''} onChange={v => setForm(f => ({ ...f, phone: v }))} />
        <Field label="Support Hours" value={form.support_hours || ''} onChange={v => setForm(f => ({ ...f, support_hours: v }))} />
        <Field label="Address" value={form.address || ''} onChange={v => setForm(f => ({ ...f, address: v }))} />
      </div>
      <SaveBtn onClick={() => onSave(form)} saving={saving} />
    </TabWrapper>
  );
}

// ── Policies Tab ────────────────────────────────────────────────────
function PoliciesTab({ data, onSave, saving }) {
  const [form, setForm] = useState(data || {});
  useEffect(() => { if (data) setForm(data); }, [data]);

  return (
    <TabWrapper title="Policies" subtitle="Refund, shipping, and privacy policies.">
      <Field label="Refund Policy" value={form.refund || ''} onChange={v => setForm(f => ({ ...f, refund: v }))} multiline />
      <Field label="Shipping Policy" value={form.shipping || ''} onChange={v => setForm(f => ({ ...f, shipping: v }))} multiline />
      <Field label="Privacy Policy Summary" value={form.privacy || ''} onChange={v => setForm(f => ({ ...f, privacy: v }))} multiline />
      <SaveBtn onClick={() => onSave(form)} saving={saving} />
    </TabWrapper>
  );
}

// ── Products Tab ────────────────────────────────────────────────────
function ProductsTab({ data, refresh, showToast }) {
  const [items, setItems] = useState(data || []);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);

  useEffect(() => { if (data) setItems(data); }, [data]);

  const blankForm = { name: '', description: '', features: '', pricing: '', url: '' };

  const startAdd = () => { setForm(blankForm); setAdding(true); setEditId(null); };
  const startEdit = (item) => { setForm({ ...item, features: (item.features || []).join(', ') }); setEditId(item.id); setAdding(false); };
  const cancel = () => { setEditId(null); setAdding(false); };

  const saveAdd = async () => {
    try {
      const payload = { ...form, features: form.features.split(',').map(f => f.trim()).filter(Boolean) };
      await api.post('/api/admin/kb/products', payload);
      showToast('Product added');
      refresh(); setAdding(false);
    } catch { showToast('Failed to add product', 'error'); }
  };

  const saveEdit = async (id) => {
    try {
      const payload = { ...form, features: form.features.split(',').map(f => f.trim()).filter(Boolean) };
      await api.put(`/api/admin/kb/products/${id}`, payload);
      showToast('Product updated');
      refresh(); setEditId(null);
    } catch { showToast('Failed to update product', 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/api/admin/kb/products/${id}`);
      showToast('Product deleted');
      refresh();
    } catch { showToast('Failed to delete', 'error'); }
  };

  return (
    <TabWrapper
      title="Products & Services"
      subtitle="Manage what you offer. The AI uses these details to answer product questions."
      action={<button className="admin-btn admin-btn--primary admin-btn--sm" onClick={startAdd}><Plus size={14} /> Add Product</button>}
    >
      {adding && (
        <ProductForm form={form} setForm={setForm} onSave={saveAdd} onCancel={cancel} label="Add Product" />
      )}
      <div className="admin-list">
        {items.length === 0 && !adding && <EmptyState msg="No products yet. Add your first product." />}
        {items.map(item => (
          <div key={item.id} className="admin-list-item">
            {editId === item.id ? (
              <ProductForm form={form} setForm={setForm} onSave={() => saveEdit(item.id)} onCancel={cancel} label="Save Changes" />
            ) : (
              <div className="admin-list-item-body">
                <div className="admin-list-item-header">
                  <span className="admin-list-item-title">{item.name}</span>
                  <div className="admin-list-item-actions">
                    <button className="admin-icon-btn" onClick={() => startEdit(item)} title="Edit"><Edit2 size={14} /></button>
                    <button className="admin-icon-btn admin-icon-btn--danger" onClick={() => del(item.id)} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
                <p className="admin-list-item-desc">{item.description}</p>
                {item.pricing && <span className="admin-badge">{item.pricing}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </TabWrapper>
  );
}

function ProductForm({ form, setForm, onSave, onCancel, label }) {
  return (
    <div className="admin-form-card">
      <div className="admin-grid-2">
        <Field label="Name *" value={form.name || ''} onChange={v => setForm(f => ({ ...f, name: v }))} />
        <Field label="Pricing" value={form.pricing || ''} onChange={v => setForm(f => ({ ...f, pricing: v }))} placeholder="e.g. $29/month" />
        <Field label="Website URL" value={form.url || ''} onChange={v => setForm(f => ({ ...f, url: v }))} />
        <Field label="Features (comma-separated)" value={form.features || ''} onChange={v => setForm(f => ({ ...f, features: v }))} placeholder="Feature 1, Feature 2" />
      </div>
      <Field label="Description *" value={form.description || ''} onChange={v => setForm(f => ({ ...f, description: v }))} multiline />
      <div className="admin-form-actions">
        <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={onSave}><Save size={14} /> {label}</button>
      </div>
    </div>
  );
}

// ── FAQs Tab ────────────────────────────────────────────────────────
function FAQsTab({ data, refresh, showToast }) {
  const [items, setItems] = useState(data || []);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);

  useEffect(() => { if (data) setItems(data); }, [data]);

  const startAdd = () => { setForm({ question: '', answer: '' }); setAdding(true); setEditId(null); };
  const startEdit = (item) => { setForm({ ...item }); setEditId(item.id); setAdding(false); };
  const cancel = () => { setEditId(null); setAdding(false); };

  const saveAdd = async () => {
    try {
      await api.post('/api/admin/kb/faqs', form);
      showToast('FAQ added'); refresh(); setAdding(false);
    } catch { showToast('Failed to add FAQ', 'error'); }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/api/admin/kb/faqs/${id}`, form);
      showToast('FAQ updated'); refresh(); setEditId(null);
    } catch { showToast('Failed to update FAQ', 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await api.delete(`/api/admin/kb/faqs/${id}`);
      showToast('FAQ deleted'); refresh();
    } catch { showToast('Failed to delete', 'error'); }
  };

  return (
    <TabWrapper
      title="FAQs"
      subtitle="Common questions and answers. The AI uses these for precise responses."
      action={<button className="admin-btn admin-btn--primary admin-btn--sm" onClick={startAdd}><Plus size={14} /> Add FAQ</button>}
    >
      {adding && (
        <FAQForm form={form} setForm={setForm} onSave={saveAdd} onCancel={cancel} label="Add FAQ" />
      )}
      <div className="admin-list">
        {items.length === 0 && !adding && <EmptyState msg="No FAQs yet. Add your first Q&A pair." />}
        {items.map(item => (
          <div key={item.id} className="admin-list-item">
            {editId === item.id ? (
              <FAQForm form={form} setForm={setForm} onSave={() => saveEdit(item.id)} onCancel={cancel} label="Save Changes" />
            ) : (
              <div className="admin-list-item-body">
                <div className="admin-list-item-header">
                  <span className="admin-list-item-title">Q: {item.question}</span>
                  <div className="admin-list-item-actions">
                    <button className="admin-icon-btn" onClick={() => startEdit(item)}><Edit2 size={14} /></button>
                    <button className="admin-icon-btn admin-icon-btn--danger" onClick={() => del(item.id)}><Trash2 size={14} /></button>
                  </div>
                </div>
                <p className="admin-list-item-desc">A: {item.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </TabWrapper>
  );
}

function FAQForm({ form, setForm, onSave, onCancel, label }) {
  return (
    <div className="admin-form-card">
      <Field label="Question *" value={form.question || ''} onChange={v => setForm(f => ({ ...f, question: v }))} />
      <Field label="Answer *" value={form.answer || ''} onChange={v => setForm(f => ({ ...f, answer: v }))} multiline />
      <div className="admin-form-actions">
        <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={onSave}><Save size={14} /> {label}</button>
      </div>
    </div>
  );
}

// ── Custom Sections Tab ─────────────────────────────────────────────
function SectionsTab({ data, refresh, showToast }) {
  const [items, setItems] = useState(data || []);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);

  useEffect(() => { if (data) setItems(data); }, [data]);

  const startAdd = () => { setForm({ title: '', content: '' }); setAdding(true); setEditId(null); };
  const startEdit = (item) => { setForm({ ...item }); setEditId(item.id); setAdding(false); };
  const cancel = () => { setEditId(null); setAdding(false); };

  const saveAdd = async () => {
    try {
      await api.post('/api/admin/kb/sections', form);
      showToast('Section added'); refresh(); setAdding(false);
    } catch { showToast('Failed to add section', 'error'); }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/api/admin/kb/sections/${id}`, form);
      showToast('Section updated'); refresh(); setEditId(null);
    } catch { showToast('Failed to update section', 'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await api.delete(`/api/admin/kb/sections/${id}`);
      showToast('Section deleted'); refresh();
    } catch { showToast('Failed to delete', 'error'); }
  };

  return (
    <TabWrapper
      title="Custom Sections"
      subtitle="Add any additional information: terms, integrations, special notes, etc."
      action={<button className="admin-btn admin-btn--primary admin-btn--sm" onClick={startAdd}><Plus size={14} /> Add Section</button>}
    >
      {adding && (
        <SectionForm form={form} setForm={setForm} onSave={saveAdd} onCancel={cancel} label="Add Section" />
      )}
      <div className="admin-list">
        {items.length === 0 && !adding && <EmptyState msg="No custom sections yet. Add extra knowledge here." />}
        {items.map(item => (
          <div key={item.id} className="admin-list-item">
            {editId === item.id ? (
              <SectionForm form={form} setForm={setForm} onSave={() => saveEdit(item.id)} onCancel={cancel} label="Save Changes" />
            ) : (
              <div className="admin-list-item-body">
                <div className="admin-list-item-header">
                  <span className="admin-list-item-title">{item.title}</span>
                  <div className="admin-list-item-actions">
                    <button className="admin-icon-btn" onClick={() => startEdit(item)}><Edit2 size={14} /></button>
                    <button className="admin-icon-btn admin-icon-btn--danger" onClick={() => del(item.id)}><Trash2 size={14} /></button>
                  </div>
                </div>
                <p className="admin-list-item-desc">{item.content?.substring(0, 100)}{item.content?.length > 100 ? '…' : ''}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </TabWrapper>
  );
}

function SectionForm({ form, setForm, onSave, onCancel, label }) {
  return (
    <div className="admin-form-card">
      <Field label="Section Title *" value={form.title || ''} onChange={v => setForm(f => ({ ...f, title: v }))} />
      <Field label="Content *" value={form.content || ''} onChange={v => setForm(f => ({ ...f, content: v }))} multiline rows={6} />
      <div className="admin-form-actions">
        <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={onSave}><Save size={14} /> {label}</button>
      </div>
    </div>
  );
}

// ── Shared Components ───────────────────────────────────────────────
function TabWrapper({ title, subtitle, children, action }) {
  return (
    <div className="admin-tab">
      <div className="admin-tab-header">
        <div>
          <h2 className="admin-tab-title">{title}</h2>
          {subtitle && <p className="admin-tab-sub">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="admin-tab-body">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, rows = 3, placeholder }) {
  return (
    <div className="admin-field">
      <label className="admin-label">{label}</label>
      {multiline ? (
        <textarea
          className="admin-input admin-input--textarea"
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
        />
      ) : (
        <input
          className="admin-input"
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function SaveBtn({ onClick, saving }) {
  return (
    <div className="admin-form-actions" style={{ marginTop: '24px' }}>
      <button className="admin-btn admin-btn--primary" onClick={onClick} disabled={saving}>
        {saving ? <Loader2 size={15} className="admin-spin" /> : <Save size={15} />}
        {saving ? 'Saving…' : 'Save Changes'}
      </button>
    </div>
  );
}

function EmptyState({ msg }) {
  return <div className="admin-empty">{msg}</div>;
}
